import { UserGroupFindDto, type UserGroupFindInterface } from '@zaoblako/shared/modules/security/dtos/user-group/user-group-find.dto';
import { UserFindDto, type UserFindInterface } from '@zaoblako/shared/modules/security/dtos/user/user-find.dto';
import { MemberFindDto, type MemberFindInterface } from '@zaoblako/shared/modules/security/dtos/member-find.dto';
import { ApprovalSchemeTypeEnum } from '@zaoblako/shared/modules/catalog/enums/approval-scheme-type.enum';
import { SerdeDtoClass } from '@zaoblako/shared/modules/serde/classes/serde-dto.class';
import { CatalogDtosKeysEnum } from '@zaoblako/shared/modules/catalog/enums/catalog-dtos-keys.enum';

import * as yup from 'yup';

/** Approval scheme response body */
export interface ApprovalSchemeFindInterface {
  /** Unique identifier. Cannot be changed. */
  id: number;
  // Tech columns
  /** Scheme creation date and time */
  created: Date;
  /** Scheme last update date and time */
  updated: Date;
  /** Scheme soft deletion date and time (`null` for not deleted schemes) */
  deleted?: Date;
  /** Scheme creator */
  createdBy?: UserFindInterface;
  // Data columns
  /** Schema display name */
  name: string;
  /** Is this scheme default for tenant */
  isDefault: boolean;
  /** Approval scheme type - determines in what order the approval will take place */
  type: ApprovalSchemeTypeEnum;
  /** Scheme approvers as users list */
  users: UserFindInterface[];
  /** User auditors (default user auditors for entities which use this approval scheme) */
  userAuditors: UserFindInterface[];
  /** User group auditors (default user group auditors for entities which use this approval scheme) */
  userGroupAuditors: UserGroupFindInterface[];
  /** All auditors */
  auditors?: MemberFindInterface[];
  /** Users granted access to this approval scheme */
  userAccess: UserFindInterface[];
  /** User groups granted access to this approval scheme */
  groupAccess: UserGroupFindInterface[];
  /** All members access granted access to this approval scheme */
  access?: MemberFindInterface[];
}

/** Approval scheme response body - DTO schema */
export const ApprovalSchemeFindDto = SerdeDtoClass.create<ApprovalSchemeFindInterface>(CatalogDtosKeysEnum.APPROVAL_SCHEME_FIND_DTO, {
  id: {
    label: 'Номер',
    serializer: yup.number(),
  },
  created: {
    label: 'Создана',
    serializer: yup.date(),
  },
  updated: {
    label: 'Изменена',
    serializer: yup.date(),
  },
  deleted: {
    optional: true,
    label: 'Удалена',
    serializer: yup.date(),
  },
  createdBy: {
    optional: true,
    label: 'Автор',
    nested: UserFindDto,
    serializer: (_, nested) => nested,
  },
  name: {
    sortable: true,
    label: 'Название',
    serializer: yup.string(),
  },
  isDefault: {
    label: 'Схема согласования по-умолчанию',
    serializer: yup.boolean(),
  },
  type: {
    sortable: true,
    label: 'Тип согласования',
    serializer: yup
      .string()
      .oneOf(Object.values(ApprovalSchemeTypeEnum)),
  },
  users: {
    label: 'Согласователи',
    nested: UserFindDto,
    serializer: (_, nested) => yup.array(nested),
  },
  userAuditors: {
    label: 'Пользователи-аудиторы',
    nested: UserFindDto,
    serializer: (_, nested) => yup.array(nested),
  },
  userGroupAuditors: {
    label: 'Группы-аудиторы',
    nested: UserGroupFindDto,
    serializer: (_, nested) => yup.array(nested),
  },
  auditors: {
    optional: true,
    label: 'Аудиторы',
    hint: 'Пользователи-аудиторы и группы-аудиторы "одним списком"',
    nested: MemberFindDto,
    serializer: (_, nested) => yup.array(nested),
  },
  userAccess: {
    label: 'Схема доступна пользователям',
    nested: UserFindDto,
    serializer: (_, nested) => yup.array(nested),
  },
  groupAccess: {
    label: 'Схема доступна группам пользователей',
    nested: UserGroupFindDto,
    serializer: (_, nested) => yup.array(nested),
  },
  access: {
    optional: true,
    label: 'Доступ предоставлен',
    hint: 'Пользователи и группы с доступом "одним списком"',
    nested: MemberFindDto,
    serializer: (_, nested) => yup.array(nested),
  },
});
